import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Turnstile } from "@/components/Turnstile";
import { getInvite, acceptInvite, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RefreshCw, Users } from "lucide-react";

const field = "w-full border-2 border-zinc-300 focus:border-[#2F6DA3] focus:outline-none px-4 py-3 text-sm transition-colors";
const label = "block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-1.5";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null); // null loading, false invalid, object valid
  const [inviteError, setInviteError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cfToken, setCfToken] = useState("");
  const onVerify = useCallback((t) => setCfToken(t), []);
  const widget = useRef(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setInvite(false); setInviteError("This invitation link is missing its token."); return; }
    getInvite(token)
      .then(setInvite)
      .catch((err) => { setInvite(false); setInviteError(formatApiError(err.response?.data?.detail) || err.message); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cfToken) { setError("Please complete the anti-bot check below."); return; }
    setLoading(true); setError("");
    try {
      const u = await acceptInvite({ token, name, password, turnstile_token: cfToken });
      setUser(u);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      widget.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (invite === null) {
    return <AuthLayout><div className="text-center"><RefreshCw size={26} className="text-[#2F6DA3] animate-spin mx-auto" /></div></AuthLayout>;
  }
  if (invite === false) {
    return (
      <AuthLayout>
        <div className="text-center" data-testid="invite-invalid">
          <h1 className="font-display text-2xl font-extrabold tracking-tight mb-2">Invitation unavailable</h1>
          <p className="text-sm text-zinc-500 mb-6">{inviteError}</p>
          <Link to="/login"><Button className="bg-[#2F6DA3] text-white font-bold uppercase tracking-wider">Go to login</Button></Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex items-center gap-2 text-[#2F6DA3] mb-3"><Users size={18} /><span className="text-[11px] font-mono uppercase tracking-[0.18em] font-bold">Team invitation</span></div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Join {invite.team_name}</h1>
      <p className="text-sm text-zinc-500 mb-8">{invite.invited_by} invited <strong>{invite.email}</strong>. Set your name and a password to join.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <div>
          <label className={label}>Your name</label>
          <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} required className={field} placeholder="Jane Smith" />
        </div>
        <div>
          <label className={label}>Password</label>
          <input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className={field} placeholder="At least 8 characters" />
        </div>
        {error && <div data-testid="invite-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
        <Turnstile ref={widget} action="invite_accept" onVerify={onVerify} />
        <Button type="submit" disabled={loading} data-testid="invite-submit" className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider py-6">
          {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : null} Join team
        </Button>
      </form>
    </AuthLayout>
  );
}
